import { WatchfaceService } from '@/services/watchface';
import { omit } from 'lodash-es';
import { defineStore } from 'pinia';
import { useWatchfaceStore } from './use-watchface-store';

export type TranslationLocale = keyof typeof BASE_LANGUAGES;
export interface TranslationStringItems {
  [id: string]: {
    [lang in TranslationLocale]?: string;
  };
}
export type I18nData = {
  translations: TranslationStringItems;
  locales: TranslationLocale[];
};
export const getRawI18n = (i18n: I18nData): I18nData => {
  const locales = i18n.locales.map((l) => l);
  const translations: TranslationStringItems = {};
  for (const id of Object.keys(i18n.translations)) {
    const rec: Partial<Record<TranslationLocale, string>> = {};
    for (const lang of Object.keys(
      i18n.translations[id]
    ) as TranslationLocale[])
      rec[lang] = i18n.translations[id][lang];
    translations[id] = rec;
  }
  return { locales, translations };
};

export const useTranslationStore = defineStore('translation-store', {
  state: () => ({
    translations: {} as TranslationStringItems,
    locales: ['zh_CN'] as TranslationLocale[]
  }),
  getters: {
    /**
     * 获取字符串的值
     * @param id 多语言字符串的ID
     * @param lang 需要获取的语言
     * @returns
     */
    getTranslation(state) {
      return (id: string, lang: TranslationLocale) =>
        state.translations[id]?.[lang] ?? '';
    },
    getRawI18n(state) {
      return getRawI18n({
        locales: state.locales,
        translations: state.translations
      });
    }
  },
  actions: {
    /**
     * 设置启用的语言(修改前后都启用的语言, 字符串保持不变; 修改后不启用的语言, 字符串全部删除; 修改后启用的语言, 字符串默认为空)
     * @param locales 启用的语言
     * @returns
     */
    setLocales(_locales: TranslationLocale[]) {
      const locales: TranslationLocale[] = Array.from(new Set([..._locales]));
      for (const id of Object.keys(this.translations) as TranslationLocale[]) {
        const res: { [lang in TranslationLocale]?: string } = {};
        for (const key of locales) res[key] = this.translations[id][key] ?? '';
        this.translations[id] = res;
      }
      this.locales = locales;
    },
    /**
     * 添加多语言字符串
     * @param id 多语言字符串的ID
     * @returns
     */
    addTranslation(id: string) {
      if (Object.keys(this.translations).includes(id))
        throw '重复添加translation id';
      const res: { [lang in TranslationLocale]?: string } = {};
      for (const key of this.locales) res[key] = '';
      this.translations[id] = res;
    },
    /**
     * 移除多语言字符串
     * @param id 多语言字符串的ID
     * @returns
     */
    removeTranslation(id: string) {
      this.translations = { ...omit(this.translations, id) };
    },
    /**
     * 设置字符串的值
     * @param id 多语言字符串的ID
     * @param lang 需要设置的语言
     * @param value 设置的字符串值
     * @returns
     */
    setTranslation(id: string, lang: TranslationLocale, value: string) {
      if (!this.translations[id]) throw '指定的语言字符串id不存在';
      if (!this.locales.includes(lang)) throw '指定的语言未启用或不存在';
      this.translations[id][lang] = value;
    },
    /**
     * 从config同步多语言数据
     * @param i18n config中的多语言数据
     * @returns
     */
    async sync(i18n?: {
      translations: TranslationStringItems;
      locales: TranslationLocale[];
    }) {
      if (i18n) {
        [this.translations, this.locales] = [
          i18n.translations ?? {},
          i18n.locales ?? []
        ];
        return;
      }
      const res = await new WatchfaceService().editorConfig.getEditorData();
      [this.translations, this.locales] = [
        res?.i18n?.translations ?? {},
        res?.i18n?.locales ?? []
      ];
    },
    /**
     * 保存多语言数据到config
     * @returns
     */
    async save(i18n?: I18nData) {
      // TODO:
      const _i18n = i18n
        ? i18n
        : { translations: this.translations, locales: this.locales };
      await new WatchfaceService().editorConfig.writeEditorData({
        i18n: getRawI18n(_i18n)
      });
    },
    /**
     * 清空多语言数据
     * @returns
     */
    clear() {
      this.translations = {};
    }
  }
});

const BASE_LANGUAGES = {
  zh_CN: '简体中文',
  en_US: '英语',
  zh_TW: '繁体中文',
  ja_JP: '日语',
  es_ES: '⻄班牙',
  fr_FR: '法国',
  de_DE: '德语',
  ru_RU: '俄罗斯',
  pt_BR: '巴⻄',
  pt_PT: '葡萄牙',
  it_IT: '意大利',
  ko_KR: '韩国',
  tr_TR: '土耳其',
  nl_NL: '荷兰',
  th_TH: '泰国',
  sv_SE: '瑞典',
  da_DK: '丹⻨',
  vi_VN: '越南',
  nb_NO: '挪威',
  pl_PL: '波兰',
  fi_FI: '芬兰',
  in_ID: '印尼',
  el_GR: '希腊',
  ro_RO: '罗⻢尼亚',
  cs_CZ: '捷克',
  uk_UA: '乌克兰',
  sk_SK: '斯洛伐克 ',
  hu_HU: '匈牙利',
  ar_EG: '阿拉伯',
  iw_IL: '希伯来',
  zh_HK: '香港繁体',
  lt_LT: '立陶宛',
  bg_BG: '保加利亚'
} as const;

const DISABLED_DEVICE_LANGUAGES: Record<string, string[]> = {
  N65: ['sk_SK', 'hu_HU', 'ar_EG', 'iw_IL', 'zh_HK'],
  M67: ['sk_SK', 'hu_HU', 'ar_EG', 'iw_IL', 'zh_HK']
};

export const getLanguages = () => {
  const watchfaceStore = useWatchfaceStore();
  const deviceRegion = watchfaceStore.description.deviceRegion ?? 'domestic';
  const { deviceType } = watchfaceStore;

  let entries = Object.entries(BASE_LANGUAGES);
  if (deviceRegion === 'international') {
    const enIndex = entries.findIndex(([key]) => key === 'en_US');
    if (enIndex > 0) {
      const [enEntry] = entries.splice(enIndex, 1);
      entries.unshift(enEntry);
    }
  }
  if (Object.keys(DISABLED_DEVICE_LANGUAGES).includes(deviceType)) {
    entries = entries.filter(
      ([key]) => !DISABLED_DEVICE_LANGUAGES[deviceType].includes(key)
    );
  }

  return Object.fromEntries(entries) as typeof BASE_LANGUAGES;
};
